import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Bot, ChevronDown, ChevronUp } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const STATUS = {
  new: "border-amber-700/50 bg-amber-950/40 text-amber-400",
  replied: "border-emerald-700/50 bg-emerald-950/40 text-emerald-400",
  closed: "border-slate-700 bg-slate-900 text-slate-400",
};

export function ContactInboxPanel() {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = () => api.get("/platform/contact-messages").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (m, status) => {
    try {
      await api.put(`/platform/contact-messages/${m.id}`, { status });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const unread = items.filter((m) => m.status === "new").length;

  return (
    <div data-testid="contact-inbox-panel" className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-5 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-heading font-bold">
          <Mail className="h-4 w-4 text-[#0A8CF2]" /> Contact inbox
          {unread > 0 && <span data-testid="contact-unread-count" className="text-[10px] font-mono-tech px-2 py-0.5 rounded-full bg-amber-500 text-black font-bold">{unread} new</span>}
        </div>
        <span className="text-[11px] text-slate-500">contact@vinconex.com · AI sends the first reply</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-500 mt-3">No messages yet — the public Contact page feeds this inbox.</p>
      ) : (
        <div className="mt-4 divide-y divide-slate-800">
          {items.slice(0, 20).map((m) => (
            <div key={m.id} data-testid={`contact-msg-${m.id}`} className="py-3">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{m.name}</span>
                    <span className="text-xs text-slate-500">{m.email}{m.dealership ? ` · ${m.dealership}` : ""}</span>
                    <Badge variant="outline" className={`${STATUS[m.status] || STATUS.new} text-[10px] font-mono-tech uppercase`}>{m.status}</Badge>
                    {m.ai_reply && <span className="text-[10px] text-[#7FC3FF] flex items-center gap-1"><Bot className="h-3 w-3" /> AI replied</span>}
                  </div>
                  <p className={`text-sm text-slate-300 mt-1 ${openId === m.id ? "" : "line-clamp-2"}`}>{m.message}</p>
                  {openId === m.id && m.ai_reply && (
                    <div className="mt-2 rounded-lg border border-[#0A8CF2]/30 bg-[#0A8CF2]/10 p-3 text-xs text-slate-200 whitespace-pre-wrap" data-testid={`contact-ai-reply-${m.id}`}>
                      <div className="text-[10px] uppercase tracking-widest text-[#7FC3FF] font-mono-tech mb-1">AI first reply</div>
                      {m.ai_reply}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] text-slate-500">{new Date(m.created_at).toLocaleDateString()}</span>
                  <select
                    value={m.status}
                    onChange={(e) => setStatus(m, e.target.value)}
                    data-testid={`contact-status-${m.id}`}
                    className="h-7 rounded bg-slate-950 border border-slate-800 text-xs px-1.5 text-slate-200"
                  >
                    <option value="new">new</option>
                    <option value="replied">replied</option>
                    <option value="closed">closed</option>
                  </select>
                  <button onClick={() => setOpenId(openId === m.id ? null : m.id)} data-testid={`contact-toggle-${m.id}`} className="text-slate-500 hover:text-white">
                    {openId === m.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
